/**
 * Leave/PTO React Query Hooks
 *
 * Custom hooks for leave operations using TanStack Query
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createPolicy, listPolicies, getPolicy, updatePolicy, assignPolicy, createLeaveRequest, listLeaveRequests, listPendingLeaveRequests, getApprovalInbox, reviewLeaveRequest, cancelLeaveRequest, getBalance, getBalances, adjustBalance, getLedgerHistory, getEmployeePolicyAssignments, getPolicyAssignments, } from '../lib/api/leave.client';
// ============================================================================
// Policy Management Hooks (Admin)
// ============================================================================
/**
 * Create a new leave policy
 */
export function useCreatePolicy() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (request) => createPolicy(request),
        onSuccess: () => {
            // Invalidate policies list
            void queryClient.invalidateQueries({ queryKey: ['leave', 'policies'] });
        },
    });
}
/**
 * List all leave policies
 */
export function useListPolicies(request) {
    return useQuery({
        queryKey: ['leave', 'policies', request],
        queryFn: () => listPolicies(request),
        retry: (failureCount, error) => {
            // Don't retry on 404s (no policies exist yet) or 403s (unauthorized)
            if (error instanceof Error && (error.message.includes('404') || error.message.includes('403'))) {
                return false;
            }
            return failureCount < 3;
        },
    });
}
/**
 * Get a specific policy
 */
export function useGetPolicy(policyId) {
    return useQuery({
        queryKey: ['leave', 'policies', policyId],
        queryFn: () => getPolicy(policyId),
        enabled: !!policyId,
    });
}
/**
 * Update an existing leave policy
 */
export function useUpdatePolicy() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ policyId, request }) => updatePolicy(policyId, request),
        onSuccess: (_, variables) => {
            // Invalidate policies list and the specific policy
            void queryClient.invalidateQueries({ queryKey: ['leave', 'policies'] });
            void queryClient.invalidateQueries({ queryKey: ['leave', 'policies', variables.policyId] });
        },
    });
}
/**
 * Assign a policy to an employee
 */
export function useAssignPolicy() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ employeeId, request }) => assignPolicy(employeeId, request),
        onSuccess: (_, variables) => {
            // Invalidate employee's balances and policy assignments
            void queryClient.invalidateQueries({ queryKey: ['leave', 'balances', variables.employeeId] });
            void queryClient.invalidateQueries({ queryKey: ['leave', 'policy-assignments', variables.employeeId] });
        },
    });
}
// ============================================================================
// Leave Request Hooks (Employee)
// ============================================================================
/**
 * Create a leave request
 */
export function useCreateLeaveRequest() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (request) => createLeaveRequest(request),
        onSuccess: () => {
            // Invalidate requests list and approval inbox
            void queryClient.invalidateQueries({ queryKey: ['leave', 'requests'] });
            void queryClient.invalidateQueries({ queryKey: ['leave', 'approvals'] });
        },
    });
}
/**
 * List leave requests
 */
export function useListLeaveRequests(request) {
    return useQuery({
        queryKey: ['leave', 'requests', request],
        queryFn: () => listLeaveRequests(request),
    });
}
/**
 * Every pending request in the tenant (admin only)
 *
 * Keyed under ['leave', 'requests'], so creating, cancelling or reviewing a
 * request refreshes it along with the other request lists.
 */
export function usePendingLeaveRequests() {
    return useQuery({
        queryKey: ['leave', 'requests', 'pending', 'tenant'],
        queryFn: () => listPendingLeaveRequests(),
        staleTime: 60 * 1000,
    });
}
// ============================================================================
// Manager Approval Hooks
// ============================================================================
/**
 * Get manager's approval inbox
 */
export function useGetApprovalInbox(request) {
    return useQuery({
        queryKey: ['leave', 'approvals', request],
        queryFn: () => getApprovalInbox(request),
        retry: (failureCount, error) => {
            // Don't retry on 404s (no approvals exist yet) or 403s (not a manager)
            if (error instanceof Error && (error.message.includes('404') || error.message.includes('403'))) {
                return false;
            }
            return failureCount < 3;
        },
    });
}
/**
 * Review a leave request (approve/reject)
 */
export function useReviewLeaveRequest() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ requestId, employeeId, request, }) => reviewLeaveRequest(requestId, employeeId, request),
        onSuccess: (_, variables) => {
            // Invalidate approval inbox, requests list, and employee's balance
            void queryClient.invalidateQueries({ queryKey: ['leave', 'approvals'] });
            void queryClient.invalidateQueries({ queryKey: ['leave', 'requests'] });
            // The admin "All Requests" page fans out its own per-employee queries
            void queryClient.invalidateQueries({ queryKey: ['admin', 'leave'] });
            void queryClient.invalidateQueries({ queryKey: ['leave', 'balances', variables.employeeId] });
            void queryClient.invalidateQueries({ queryKey: ['leave', 'ledger', variables.employeeId] });
        },
    });
}
/**
 * Cancel a leave request (employee only - own pending requests)
 */
export function useCancelLeaveRequest() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (requestId) => cancelLeaveRequest(requestId),
        onSuccess: () => {
            // Invalidate requests list (My Requests page will update)
            void queryClient.invalidateQueries({ queryKey: ['leave', 'requests'] });
            // Invalidate approval inbox (manager's view will update)
            void queryClient.invalidateQueries({ queryKey: ['leave', 'approvals'] });
        },
    });
}
// ============================================================================
// Balance Hooks
// ============================================================================
/**
 * Get employee's balance for a specific leave type
 */
export function useGetBalance(employeeId, leaveType) {
    return useQuery({
        queryKey: ['leave', 'balances', employeeId, leaveType],
        queryFn: () => getBalance(employeeId, leaveType),
        enabled: !!employeeId && !!leaveType,
    });
}
/**
 * Get all balances for an employee
 * If employeeId is not provided, fetches balances for the current user
 */
export function useGetBalances(employeeId) {
    return useQuery({
        queryKey: ['leave', 'balances', employeeId || 'me'],
        queryFn: () => getBalances(employeeId),
        enabled: true,
    });
}
/**
 * Manually adjust employee's balance (admin only)
 */
export function useAdjustBalance() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ employeeId, leaveType, request, }) => adjustBalance(employeeId, leaveType, request),
        onSuccess: (_, variables) => {
            // Invalidate employee's balances and ledger
            void queryClient.invalidateQueries({ queryKey: ['leave', 'balances', variables.employeeId] });
            void queryClient.invalidateQueries({ queryKey: ['leave', 'ledger', variables.employeeId] });
        },
    });
}
// ============================================================================
// Ledger Hooks
// ============================================================================
/**
 * Get ledger history for an employee's leave type
 */
export function useGetLedgerHistory(employeeId, leaveType, startDate, endDate) {
    return useQuery({
        queryKey: ['leave', 'ledger', employeeId, leaveType, startDate, endDate],
        queryFn: () => getLedgerHistory(employeeId, leaveType, startDate, endDate),
        enabled: !!employeeId && !!leaveType,
    });
}
// ============================================================================
// Policy Assignment Hooks
// ============================================================================
/**
 * Get employee's policy assignments
 */
export function useGetEmployeePolicyAssignments(employeeId) {
    return useQuery({
        queryKey: ['leave', 'policy-assignments', employeeId],
        queryFn: () => getEmployeePolicyAssignments(employeeId),
        enabled: !!employeeId,
    });
}
/**
 * Get all employees assigned to a policy
 */
export function useGetPolicyAssignments(policyId) {
    return useQuery({
        queryKey: ['leave', 'policy-assignments', 'policy', policyId],
        queryFn: () => getPolicyAssignments(policyId),
        enabled: !!policyId,
    });
}
